// src/components/TherapistActionModal.js
import React, { useState, useEffect } from 'react';
import { X, Calendar, Shield, Clock } from 'lucide-react';

const TherapistActionModal = ({ isOpen, onClose }) => {
  const [therapist, setTherapist] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasTherapist, setHasTherapist] = useState(false);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (isOpen) {
      checkTherapistStatus();
    }
  }, [isOpen]);

  const checkTherapistStatus = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('therapy_auth_token');
      if (!token) { setIsLoading(false); return; }

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${API_BASE}/api/invitations/my-therapist`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.has_therapist && data.therapist) {
          setTherapist(data.therapist);
          setHasTherapist(true);
        } else {
          setHasTherapist(false);
        }
      } else {
        setHasTherapist(false);
      }
    } catch (error) {
      setHasTherapist(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    try {
      setIsSending(true);
      const token = localStorage.getItem('therapy_auth_token');
      if (!token) return;

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      if (hasTherapist) {
        const response = await fetch(`${API_BASE}/api/therapist-booking/request-session`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            therapist_id: therapist?.id?.toString() || '',
            message: message || 'Jag skulle vilja boka ett samtal.',
            preferred_times: 'Flexibel med tider',
          }),
        });
        if (!response.ok) throw new Error('Något gick fel');
      } else {
        const response = await fetch(`${API_BASE}/api/therapist-matching/request-matching`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ additional_message: message || 'Förfrågan om terapeutmatchning.' }),
        });
        if (!response.ok) throw new Error('Något gick fel');
      }

      setSent(true);
    } catch (error) {
      alert(`Något gick fel: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-brand-primary-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-lg font-bold">Boka samtal med terapeut</h2>
              <p className="text-white/75 text-sm mt-0.5">Legitimerad psykoterapeut</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white hover:bg-white/20 p-1.5 rounded-full transition-colors">
              <X size={18} />
            </button>
          </div>
          {hasTherapist && therapist && (
            <div className="mt-4 bg-white/10 rounded-xl px-4 py-3">
              <p className="font-semibold text-sm">{therapist.name}</p>
              <p className="text-white/70 text-xs">{therapist.email}</p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="w-8 h-8 border-2 border-brand-primary-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-500">Hämtar information...</p>
            </div>
          ) : sent ? (
            <div className="text-center py-4">
              <div className="w-12 h-12 bg-brand-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Calendar className="text-brand-primary-600" size={24} />
              </div>
              <p className="font-semibold text-gray-900 mb-1">Förfrågan skickad!</p>
              <p className="text-sm text-gray-500 mb-6">
                {hasTherapist ? 'Din terapeut hör av sig inom 24 timmar.' : 'Vi matchar dig med en terapeut och återkommer inom 24 timmar.'}
              </p>
              <button onClick={onClose} className="w-full py-3 bg-brand-primary-600 text-white font-semibold rounded-xl hover:bg-brand-primary-700 transition-colors">
                Stäng
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-4">
                {hasTherapist
                  ? 'Skicka en bokningsförfrågan så hör din terapeut av sig inom 24 timmar för att boka tid.'
                  : 'Vi kopplar ihop dig med en legitimerad psykoterapeut som passar dina behov och återkommer inom 24 timmar.'}
              </p>

              {!hasTherapist && (
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-3 bg-brand-primary-50 rounded-xl p-3">
                    <Shield className="text-brand-primary-600 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Legitimerade psykoterapeuter</span>
                  </div>
                  <div className="flex items-center gap-3 bg-brand-primary-50 rounded-xl p-3">
                    <Calendar className="text-brand-primary-600 flex-shrink-0" size={16} />
                    <span className="text-sm text-gray-700">Fysiskt eller digitalt – du väljer</span>
                  </div>
                </div>
              )}

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Meddelande <span className="text-gray-400 font-normal">(valfritt)</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={hasTherapist ? 'Beskriv vad du vill prata om eller föreslå tider...' : 'Beskriv kortfattat vad du söker hjälp med...'}
                className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary-400 focus:border-transparent resize-none text-sm mb-5"
                rows={3}
              />

              <button
                onClick={handleSend}
                disabled={isSending}
                className="w-full py-3 bg-brand-primary-600 text-white font-semibold rounded-xl hover:bg-brand-primary-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 mb-3"
              >
                {isSending ? <><Clock className="animate-spin" size={16} /> Skickar...</> : 'Skicka förfrågan'}
              </button>
              <button onClick={onClose} className="w-full py-3 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                Stäng
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TherapistActionModal;
